import { useDeferredValue } from 'react'

import { isInteractionSchedulingEnabled } from '../utils/interaction-scheduling'

export interface DeferredModalPresence {
  /** Keep the modal shell mounted. Stays true one deferred pass after `open` flips false so the
   * heavy subtree tears down off the interaction (the shell hides visually via `open`). */
  mountOpen: boolean
  /** Render the heavy children. Stays false during the opening urgent pass so the shell (backdrop,
   * frame, close button) paints as the interaction's immediate visual response. */
  contentOpen: boolean
}

/**
 * Split a modal's `open` flag into an urgent shell state and a deferred content state so both the
 * open and the close interaction paint their visible response before the expensive subtree
 * mounts/unmounts (the INP "processing" cost measured on size-chart open and modal close).
 *
 * State walk while the scheduling gate is on:
 * - opening, urgent pass:  { mountOpen: true,  contentOpen: false } — shell paints instantly
 * - open, settled:         { mountOpen: true,  contentOpen: true }
 * - closing, urgent pass:  { mountOpen: true,  contentOpen: true }  — pass `open` (false) to the
 *   shell for the instant hide; teardown happens on the next deferred pass
 * - closed, settled:       { mountOpen: false, contentOpen: false }
 *
 * Dark-shipped: while `interaction_scheduling` is disabled both fields simply mirror `open`, so
 * behavior is identical to the previous hard `if (!open) return null` gating.
 */
export function useDeferredModalPresence(open: boolean): DeferredModalPresence {
  const deferredOpen = useDeferredValue(open)

  if (!isInteractionSchedulingEnabled()) {
    return { mountOpen: open, contentOpen: open }
  }

  return { mountOpen: open || deferredOpen, contentOpen: deferredOpen }
}
